import { emptyGuid } from "~/Types/Guid";
import { ModelBase } from "~/Generated/Entities";
import { deleteEntity, get, patch, post } from "~/Services/Request";
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";

export interface ODataParameter {
    $filter?: string;
    $expand?: string;
    $orderBy?: string;
    $top?: number;
    $skip?: number;
    $count?: boolean;
}

export const useGetModel = <T extends ModelBase>(
    id: string,
    parameter: ODataParameter | undefined,
    segment: string,
    enabled = true,
) => {
    return callUseGetQuery<T>(id, parameter, segment, {
        enabled: enabled,
    });
};

const callUseGetQuery = <T extends ModelBase>(
    id: string,
    parameter: ODataParameter | undefined,
    segment: string,
    options: Omit<UseQueryOptions<T>, "queryKey">,
) => {
    // errors don't seem to throw even with this being true
    return useQuery<T>({
        queryKey: [segment, id, parameter],
        queryFn: () => {
            return id === "new"
                ? get<T>(`/api/v1/admin/${segment}/default()`, parameter)
                : get<T>(`/api/v1/admin/${segment}(${id})`, parameter);
        },

        staleTime: 2000,
        select: (model: any) => {
            delete model["@odata.context"];
            delete model["@odata.type"];
            return model;
        },
        enabled: options.enabled,
        throwOnError: true,
    });
};

export const useGetModels = <T extends ModelBase>(
    parameter: ODataParameter | undefined,
    segment: string,
    enabled = true,
) => {
    return useQuery<{ value: T[]; count: number }>({
        queryKey: [segment, parameter],
        queryFn: () => get<{ value: T[]; count: number }>("/api/v1/admin/" + segment, parameter),
        enabled,
        select: data => {
            // @ts-ignore
            data.count = data["@odata.count"];
            return data;
        },
    });
};

export const useUpdateModel = <T extends ModelBase>(segment: string) => {
    const queryClient = useQueryClient();
    const { displayError } = useErrorHandler();
    return useMutation<any, any, T, any>({
        mutationFn: async model => {
            const newModel = await updateModel(model, segment);
            model.id = newModel.id;
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: [segment] });
        },
        onError: (error, variables, context) => {
            displayError(error);
        },
    });
};

const updateModel = <T extends ModelBase>(model: T, segment: string) => {
    let endpoint = "/api/v1/admin/" + segment;
    if (model.id !== emptyGuid && !!model.id) {
        endpoint += `(${model.id})`;
        return patch<T>(endpoint, model);
    } else {
        return post<T>(endpoint, model);
    }
};

function useErrorHandler() {
    return {
        displayError: (error: any) => {
            console.log(error);
        },
    };
}

export const useDeleteModel = <T extends ModelBase>(segment: string) => {
    const queryClient = useQueryClient();
    const { displayError } = useErrorHandler();
    return useMutation<any, any, T, any>({
        mutationFn: async model => {
            await deleteModel(model, segment);
        },

        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: [segment] });
        },
        onError: error => {
            displayError(error);
        },
    });
};

const deleteModel = <T extends ModelBase>(model: T, segment: string) => {
    let endpoint = "/api/v1/admin/" + segment;
    return deleteEntity<T>(endpoint, model.id);
};
